import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Calendar, Clock, Loader2, ChevronRight, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import '../styles/SlotPicker.css';

const API = process.env.REACT_APP_BACKEND_URL;

/* ============================================================
   <SlotPickerModal />
   ------------------------------------------------------------
   Two-step modal shown before Stripe Checkout:
     1. Buyer picks a slot from the on-the-fly generated
        weekday grid (Mon–Fri, 10 / 13 / 16 UK time, 6 weeks).
     2. Buyer fills name + email; we soft-hold the slot for 10
        minutes, then hand off to Stripe Checkout with the
        hold_id + slot_id attached as session metadata.
   ============================================================ */

const buyerTZ = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

const fmtInTZ = (isoUtc, tz) => {
  const d = new Date(isoUtc);
  return d.toLocaleString('en-GB', {
    timeZone: tz,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtUKDate = (isoUtc) => {
  return new Date(isoUtc).toLocaleDateString('en-GB', {
    timeZone: 'Europe/London',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
};

const fmtUKTime = (isoUtc) => {
  return new Date(isoUtc).toLocaleTimeString('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SlotPickerModal = ({ open, lookupKey, serviceLabel, priceLabel, onClose }) => {
  const [step, setStep] = useState('slots');   // slots | details | starting
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [paymentType, setPaymentType] = useState('full'); // 'full' | 'deposit'
  const [submitting, setSubmitting] = useState(false);
  const tz = useMemo(() => buyerTZ(), []);
  const showLocalTZ = tz && tz !== 'Europe/London';

  // Deposit is only available for the Executive Discovery Session AND when
  // the selected slot is 2+ business days out — matches server-side rule.
  const depositEligible = useMemo(() => {
    if (lookupKey !== 'advisory_discovery_gbp') return false;
    if (!selected) return false;
    const slot = new Date(selected.start_utc);
    // Compare against "now + 2 calendar days" as a client-side proxy
    // (the server has the authoritative business-day check).
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() + 2);
    cutoff.setHours(0, 0, 0, 0);
    return slot >= cutoff;
  }, [lookupKey, selected]);

  // If buyer flipped to deposit and then picked a next-day slot, snap back
  // to full so the UI doesn't lie about the price they'll be charged.
  useEffect(() => {
    if (paymentType === 'deposit' && !depositEligible) {
      setPaymentType('full');
    }
  }, [depositEligible, paymentType]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/bookings/slots`, {
        params: { service: lookupKey },
      });
      setSlots(data?.slots || []);
    } catch (e) {
      toast.error('Could not load available slots. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [lookupKey]);

  useEffect(() => {
    if (open) {
      setStep('slots');
      setSelected(null);
      setName('');
      setEmail('');
      setPaymentType('full');
      load();
    }
  }, [open, load]);

  // Group slots by UK date for a compact "day cards with time chips" layout.
  const grouped = useMemo(() => {
    const map = new Map();
    slots.forEach((s) => {
      const key = new Date(s.start_utc).toLocaleDateString('en-CA', {
        timeZone: 'Europe/London',
      });
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    return Array.from(map.entries()).slice(0, 30);
  }, [slots]);

  if (!open) return null;

  const proceed = async () => {
    if (!selected || !name.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      const hold = await axios.post(`${API}/api/bookings/hold`, {
        lookup_key: lookupKey,
        slot_id: selected.slot_id,
        name: name.trim(),
        email: email.trim().toLowerCase(),
      });
      const checkout = await axios.post(`${API}/api/payments/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        hold_id: hold.data.hold_id,
        slot_id: selected.slot_id,
        slot_start_utc: selected.start_utc,
        customer_name: name.trim(),
        customer_email: email.trim().toLowerCase(),
        payment_type: paymentType,
      });
      if (checkout.data?.checkout_url) {
        window.location.href = checkout.data.checkout_url;
      } else {
        toast.error('Could not open secure checkout. Please try again.');
        setSubmitting(false);
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        'Could not reserve that slot. Please pick another or try again.';
      toast.error(msg);
      setSubmitting(false);
      if (e?.response?.status === 409) load();
    }
  };

  const checkoutLabel =
    paymentType === 'deposit'
      ? 'Continue to secure checkout · £250 deposit'
      : `Continue to secure checkout · ${priceLabel}`;

  return (
    <div className="sp-overlay" role="dialog" aria-modal="true" aria-labelledby="sp-title" data-testid="slot-picker-modal">
      <div className="sp-panel">
        <button
          type="button"
          className="sp-close"
          onClick={onClose}
          aria-label="Close"
          data-testid="slot-picker-close"
        >
          <X size={22} />
        </button>

        <header className="sp-head">
          <span className="sp-eyebrow">
            <Calendar size={14} /> Book your session
          </span>
          <h2 id="sp-title" className="sp-title">{serviceLabel}</h2>
          <p className="sp-sub">
            <strong>{priceLabel}</strong> · 2-hour session ·{' '}
            <span className="sp-tz-uk">All times UK (Europe/London)</span>
            {showLocalTZ && (
              <span className="sp-tz-local"> · your time: {tz}</span>
            )}
          </p>
        </header>

        {step === 'slots' && (
          <div className="sp-body">
            {loading && (
              <div className="sp-loading">
                <Loader2 className="sp-spin" size={26} />
                <span>Loading available slots…</span>
              </div>
            )}
            {!loading && grouped.length === 0 && (
              <div className="sp-empty">
                <AlertCircle size={22} />
                <p>No slots available right now. Please try again shortly or
                email us to arrange a session.</p>
              </div>
            )}
            {!loading && grouped.length > 0 && (
              <ul className="sp-day-list" data-testid="slot-day-list">
                {grouped.map(([dayKey, daySlots]) => (
                  <li key={dayKey} className="sp-day">
                    <div className="sp-day-label">
                      {fmtUKDate(daySlots[0].start_utc)}
                    </div>
                    <div className="sp-time-row">
                      {daySlots.map((s) => {
                        const disabled = s.status !== 'available';
                        const isSelected = selected?.slot_id === s.slot_id;
                        return (
                          <button
                            key={s.slot_id}
                            type="button"
                            disabled={disabled}
                            className={`sp-time-btn ${isSelected ? 'is-selected' : ''} ${disabled ? 'is-disabled' : ''}`}
                            onClick={() => setSelected(s)}
                            data-testid={`slot-btn-${s.slot_id}`}
                          >
                            <Clock size={13} />
                            <span className="sp-time-uk">{fmtUKTime(s.start_utc)}</span>
                            {showLocalTZ && (
                              <span className="sp-time-local">
                                {fmtInTZ(s.start_utc, tz).split(' ').slice(-2).join(' ')}
                              </span>
                            )}
                            {disabled && <span className="sp-badge">{s.status}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {step === 'details' && selected && (
          <div className="sp-body">
            <div className="sp-summary">
              <div>
                <span className="sp-summary-label">Selected slot</span>
                <div className="sp-summary-value">
                  {fmtUKDate(selected.start_utc)} · {fmtUKTime(selected.start_utc)}
                  <span className="sp-tz-tag">UK</span>
                </div>
                {showLocalTZ && (
                  <div className="sp-summary-alt">
                    Your time: {fmtInTZ(selected.start_utc, tz)}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="sp-change"
                onClick={() => setStep('slots')}
              >
                Change
              </button>
            </div>
            <div className="sp-field">
              <label htmlFor="sp-name">Full name</label>
              <input
                id="sp-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
                data-testid="slot-picker-name"
              />
            </div>
            <div className="sp-field">
              <label htmlFor="sp-email">Email</label>
              <input
                id="sp-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                data-testid="slot-picker-email"
              />
            </div>
            {lookupKey === 'advisory_discovery_gbp' && (
              <div className="sp-field sp-pay-choice" data-testid="sp-pay-choice">
                <span className="sp-pay-label">Payment</span>
                <label
                  className={`sp-pay-option ${paymentType === 'full' ? 'is-selected' : ''}`}
                  data-testid="sp-pay-full"
                >
                  <input
                    type="radio"
                    name="sp-payment-type"
                    value="full"
                    checked={paymentType === 'full'}
                    onChange={() => setPaymentType('full')}
                  />
                  <span className="sp-pay-copy">
                    <strong>Pay in full · £1,250</strong>
                    <em>One payment now — nothing else to settle.</em>
                  </span>
                </label>
                <label
                  className={`sp-pay-option ${paymentType === 'deposit' ? 'is-selected' : ''} ${!depositEligible ? 'is-disabled' : ''}`}
                  data-testid="sp-pay-deposit"
                >
                  <input
                    type="radio"
                    name="sp-payment-type"
                    value="deposit"
                    checked={paymentType === 'deposit'}
                    onChange={() => depositEligible && setPaymentType('deposit')}
                    disabled={!depositEligible}
                  />
                  <span className="sp-pay-copy">
                    <strong>£250 deposit now · £1,000 balance later</strong>
                    <em>
                      {depositEligible
                        ? 'We invoice the balance closer to your session. Refundable up to 48h before, non-refundable inside 48h.'
                        : 'Sessions booked less than 2 business days out must be paid in full.'}
                    </em>
                  </span>
                </label>
              </div>
            )}
            <p className="sp-fineprint">
              Your slot is held for 10 minutes while you complete checkout.
              If payment doesn&rsquo;t complete, the slot is released back to
              the calendar.
            </p>
          </div>
        )}

        <footer className="sp-actions">
          {step === 'slots' && (
            <button
              type="button"
              className="sp-primary"
              disabled={!selected}
              onClick={() => setStep('details')}
              data-testid="slot-picker-continue"
            >
              Continue
              <ChevronRight size={16} />
            </button>
          )}
          {step === 'details' && (
            <button
              type="button"
              className="sp-primary"
              disabled={submitting || !name.trim() || !email.trim()}
              onClick={proceed}
              data-testid="slot-picker-proceed"
            >
              {submitting ? (
                <>
                  <Loader2 className="sp-spin" size={16} />
                  Opening secure checkout…
                </>
              ) : (
                <>
                  {checkoutLabel}
                  <ChevronRight size={16} />
                </>
              )}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default SlotPickerModal;
